import { useState, useEffect, useRef, useMemo, lazy, Suspense } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import type { Booking } from '../../lib/types'
import { format } from 'date-fns'
import { sortBookings } from '../../utils/sorting'
import { AdminNav, type AdminTab, TABS, getTabFromHash } from '../layout/AdminNav'
import { BookingDetailDialog } from './BookingDetailDialog'
import { PriceDropsSection } from './PriceDropsSection'
import { BookingsSection } from './BookingsSection'
import { CollapsibleBookingsSection } from './CollapsibleBookingsSection'
import { KeyboardShortcutsOverlay } from '../layout/KeyboardShortcutsOverlay'
import { ErrorState } from '../ui/ErrorState'
import { Sentry } from '../../lib/sentry'

/** Isolate each tab view's render crashes so a bug in one tab doesn't
 *  take out the entire admin (nav, sign-out, other tabs). The fallback
 *  reuses ErrorState so the operator sees the same problem/cause/fix UI
 *  as for fetch errors. resetError re-mounts the wrapped subtree. */
function TabBoundary({
  tab,
  onSignOut,
  children,
}: {
  tab: string
  onSignOut: () => void
  children: React.ReactNode
}) {
  return (
    <Sentry.ErrorBoundary
      key={tab}
      fallback={({ error, resetError }) => (
        <div className="space-y-4">
          <ErrorState
            error={error instanceof Error ? error.message : String(error)}
            onRetry={resetError}
            onSignOut={onSignOut}
          />
        </div>
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  )
}

// Code-split the secondary tabs. Cold load only pays for the dashboard
// tab — recharts alone (Research tab) is ~110KB gzipped.
const EmailLogsView = lazy(() => import('../email-logs/EmailLogsView').then(m => ({ default: m.EmailLogsView })))
const EmailTestingView = lazy(() => import('../email-testing/EmailTestingView').then(m => ({ default: m.EmailTestingView })))
const ResearchAnalyticsView = lazy(() => import('../research/ResearchAnalyticsView').then(m => ({ default: m.ResearchAnalyticsView })))
const BillingView = lazy(() => import('../billing/BillingView').then(m => ({ default: m.BillingView })))
const UserExplorerView = lazy(() => import('../users/UserExplorerView').then(m => ({ default: m.UserExplorerView })))
const OpsHealthView = lazy(() => import('../ops/OpsHealthView').then(m => ({ default: m.OpsHealthView })))

function TabFallback() {
  return (
    <div className="flex items-center justify-center py-12 text-sm text-stone-400 dark:text-stone-500">
      Loading…
    </div>
  )
}
import {
  usePriceDrops,
  useActiveBookings,
  useCollapsibleBookings,
  useSearch,
  useAdminStats,
  useSortConfig,
} from '../../hooks/useAdminData'

export function AdminDashboard({ session, user, onSignOut }: { session: Session; user: User; onSignOut: () => void }) {
  const [activeTab, setActiveTab] = useState<AdminTab>(getTabFromHash)
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const hasFetchedRef = useRef(false)
  const [visitedTabs, setVisitedTabs] = useState<Set<AdminTab>>(() => new Set([getTabFromHash()]))

  // Track which tabs have been opened at least once so we can keep them
  // mounted (and preserve their state) after the first visit, while still
  // deferring the initial code-split download until needed.
  useEffect(() => {
    setVisitedTabs(prev => (prev.has(activeTab) ? prev : new Set([...prev, activeTab])))
  }, [activeTab])

  // Data hooks
  const priceDrops = usePriceDrops(session)
  const activeBookings = useActiveBookings(session)
  const inactiveBookings = useCollapsibleBookings(session, 'inactive')
  const archivedBookings = useCollapsibleBookings(session, 'archived')
  const searchHook = useSearch(session)
  const statsHook = useAdminStats(session)
  const priceDropsSort = useSortConfig()
  const bookingsSort = useSortConfig()

  // Sync URL hash when tab changes
  useEffect(() => {
    window.location.hash = activeTab === 'dashboard' ? '' : activeTab
  }, [activeTab])

  // Handle browser back/forward navigation
  useEffect(() => {
    const onHashChange = () => setActiveTab(getTabFromHash())
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  // Initial data fetch
  useEffect(() => {
    if (!hasFetchedRef.current && session) {
      hasFetchedRef.current = true
      Promise.all([
        priceDrops.fetch(),
        activeBookings.fetch(),
        statsHook.fetch(),
      ])
    }
  }, [session])

  // Derive totals from stats. These drive the collapsible-section headers
  // directly — the prior useEffect that piped them into each hook's local
  // state was dead (the hooks never read their own `total`).
  const allBookingsTotal = statsHook.stats?.total_bookings || 0
  const activeBookingsTotal = statsHook.stats?.active_bookings || 0
  const archivedTotal = statsHook.stats?.archived_bookings || 0
  const inactiveTotal = allBookingsTotal - activeBookingsTotal

  // Auto-refresh price drops every 60 seconds
  useEffect(() => {
    if (activeTab !== 'dashboard') return
    const interval = setInterval(() => {
      if (!document.hidden) {
        priceDrops.fetch()
      }
    }, 60_000)
    return () => clearInterval(interval)
  }, [activeTab, priceDrops.fetch])

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return

      // Number keys 1..N select the Nth tab (1-indexed). Capped at 9 so
      // we never try to parse a two-digit shortcut; TABS.length is 7
      // today but the cap is defensive for future additions.
      if (e.key >= '1' && e.key <= '9') {
        const tabIndex = parseInt(e.key) - 1
        if (tabIndex < TABS.length) { e.preventDefault(); setActiveTab(TABS[tabIndex].id) }
        return
      }
      if (e.key === '/') {
        e.preventDefault()
        document.querySelector<HTMLInputElement>('[data-search-input]')?.focus()
        return
      }
      if (e.key === '?') { e.preventDefault(); setShowShortcuts(true) }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  const totalSavings = useMemo(() => priceDrops.priceDrops.reduce((sum, b) => sum + (b.potential_savings || 0), 0), [priceDrops.priceDrops])
  const sortedPriceDrops = useMemo(() => sortBookings(priceDrops.priceDrops, priceDropsSort.config), [priceDrops.priceDrops, priceDropsSort.config])
  const sortedBookings = useMemo(() => sortBookings(searchHook.results ?? activeBookings.bookings, bookingsSort.config), [searchHook.results, activeBookings.bookings, bookingsSort.config])
  const sortedInactiveBookings = useMemo(() => sortBookings(inactiveBookings.bookings, bookingsSort.config), [inactiveBookings.bookings, bookingsSort.config])
  const sortedArchivedBookings = useMemo(() => sortBookings(archivedBookings.bookings, bookingsSort.config), [archivedBookings.bookings, bookingsSort.config])

  const loading = priceDrops.loading && activeBookings.loading

  const refreshAll = () => {
    priceDrops.fetch()
    activeBookings.fetch()
    statsHook.fetch()
  }

  // Booking lifecycle callbacks
  const handleArchived = (bookingId: string) => {
    setSelectedBooking(null)
    priceDrops.remove(bookingId)
    activeBookings.remove(bookingId)
    inactiveBookings.remove(bookingId)
    searchHook.setResults(prev => prev ? prev.filter(b => b.id !== bookingId) : null)
    archivedBookings.refetch()
    statsHook.fetch()
  }

  const handleDeleted = (bookingId: string) => {
    setSelectedBooking(null)
    archivedBookings.remove(bookingId)
    searchHook.setResults(prev => prev ? prev.filter(b => b.id !== bookingId) : null)
    statsHook.fetch()
  }

  const handleReactivated = (bookingId: string) => {
    setSelectedBooking(null)
    archivedBookings.remove(bookingId)
    refreshAll()
  }

  const handleRebooked = (bookingId: string) => {
    setSelectedBooking(null)
    priceDrops.remove(bookingId)
    refreshAll()
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        <div>
          <div className="w-24 h-3 bg-stone-200 dark:bg-stone-800 rounded animate-pulse" />
          <div className="w-56 h-7 bg-stone-200 dark:bg-stone-800 rounded animate-pulse mt-2" />
        </div>
        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-10 bg-stone-100 dark:bg-stone-800 rounded animate-pulse" />
            ))}
          </div>
        </div>
        <div>
          <div className="w-28 h-3 bg-stone-200 dark:bg-stone-800 rounded animate-pulse" />
          <div className="w-32 h-7 bg-stone-200 dark:bg-stone-800 rounded animate-pulse mt-2" />
        </div>
        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-6">
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-10 bg-stone-100 dark:bg-stone-800 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      <AdminNav
        activeTab={activeTab}
        onTabChange={setActiveTab}
        user={user}
        onSignOut={onSignOut}
        stats={activeTab === 'dashboard' ? {
          bookings: allBookingsTotal,
          active: activeBookingsTotal,
          drops: priceDrops.total,
          savings: totalSavings,
        } : undefined}
      />

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
      {/* Non-dashboard tabs mount on first visit and stay mounted after,
          so switching is instant but the initial load only pays for
          what the user actually opened. Each is code-split via React.lazy. */}
      <Suspense fallback={<TabFallback />}>
        {visitedTabs.has('billing') && (
          <div className={activeTab === 'billing' ? 'tab-content-enter' : 'hidden'}>
            <TabBoundary tab="billing" onSignOut={onSignOut}><BillingView session={session} /></TabBoundary>
          </div>
        )}
        {visitedTabs.has('users') && (
          <div className={activeTab === 'users' ? 'tab-content-enter' : 'hidden'}>
            <TabBoundary tab="users" onSignOut={onSignOut}><UserExplorerView session={session} /></TabBoundary>
          </div>
        )}
        {visitedTabs.has('email-logs') && (
          <div className={activeTab === 'email-logs' ? 'tab-content-enter' : 'hidden'}>
            <TabBoundary tab="email-logs" onSignOut={onSignOut}><EmailLogsView session={session} /></TabBoundary>
          </div>
        )}
        {visitedTabs.has('email-testing') && (
          <div className={activeTab === 'email-testing' ? 'tab-content-enter' : 'hidden'}>
            <TabBoundary tab="email-testing" onSignOut={onSignOut}><EmailTestingView session={session} /></TabBoundary>
          </div>
        )}
        {visitedTabs.has('ops') && (
          <div className={activeTab === 'ops' ? 'tab-content-enter' : 'hidden'}>
            <TabBoundary tab="ops" onSignOut={onSignOut}><OpsHealthView session={session} /></TabBoundary>
          </div>
        )}
        {visitedTabs.has('research') && (
          <div className={activeTab === 'research' ? 'tab-content-enter' : 'hidden'}>
            <TabBoundary tab="research" onSignOut={onSignOut}><ResearchAnalyticsView session={session} /></TabBoundary>
          </div>
        )}
      </Suspense>
      {activeTab === 'dashboard' && (
        <>
          {priceDrops.error && (
            <ErrorState
              error={priceDrops.error}
              onRetry={refreshAll}
              onSignOut={onSignOut}
            />
          )}
          <PriceDropsSection
            priceDrops={sortedPriceDrops}
            total={priceDrops.total}
            sortConfig={priceDropsSort.config}
            onSort={priceDropsSort.handleSort}
            onBookingClick={setSelectedBooking}
            onRebooked={handleRebooked}
            onRefresh={refreshAll}
            session={session}
          />

          <BookingsSection
            bookings={sortedBookings}
            totalActive={activeBookingsTotal}
            sortConfig={bookingsSort.config}
            onSort={bookingsSort.handleSort}
            searchQuery={searchHook.query}
            searching={searchHook.searching}
            hasSearchResults={searchHook.results !== null}
            onSearch={searchHook.search}
            onBookingClick={setSelectedBooking}
            onLoadMore={activeBookings.loadMore}
            loadingMore={activeBookings.loadingMore}
            loadedCount={activeBookings.bookings.length}
          />

          <CollapsibleBookingsSection
            title="Inactive Bookings"
            total={inactiveTotal}
            bookings={sortedInactiveBookings}
            expanded={inactiveBookings.expanded}
            loading={inactiveBookings.loading}
            onToggle={inactiveBookings.toggle}
            onLoadMore={inactiveBookings.loadMore}
            onBookingClick={setSelectedBooking}
          />

          <CollapsibleBookingsSection
            title="Archived Bookings"
            total={archivedTotal}
            bookings={sortedArchivedBookings}
            expanded={archivedBookings.expanded}
            loading={archivedBookings.loading}
            onToggle={archivedBookings.toggle}
            onLoadMore={archivedBookings.loadMore}
            onBookingClick={setSelectedBooking}
          />

          <p className="text-center text-xs text-stone-400 dark:text-stone-500">
            Last updated: {format(new Date(), 'MMM d, h:mm a')}
          </p>
        </>
      )}
      </main>

      {selectedBooking && (
        <BookingDetailDialog
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
          onDeleted={handleDeleted}
          onArchived={handleArchived}
          onReactivated={handleReactivated}
          onRebooked={handleRebooked}
          session={session}
        />
      )}

      {showShortcuts && (
        <KeyboardShortcutsOverlay onClose={() => setShowShortcuts(false)} />
      )}
    </div>
  )
}
